import { T } from '../theme'

/**
 * VideoScopeChoice — ce que le coach a filmé, déclaré AVANT l'upload.
 * PARCOURS-VIDEO-DECLARE-001.
 *
 * POURQUOI CE BLOC EXISTE. Le produit exigeait un fichier unique contenant le
 * match entier, ce qui suppose une caméra qu'on n'arrête jamais. Un coach qui
 * filme au téléphone sur trépied s'arrête à la pause et obtient DEUX fichiers.
 * Mesuré le 11/08/2026 · 9 matchs bloqués sur 5 clubs, dont la mi-temps unique
 * de Martin Hiblot, premier client payant, reparti sans son rapport.
 *
 * LE CHEMIN MAJORITAIRE RESTE LE PLUS COURT, et c'est un arbitrage produit
 * confirmé. Un fichier contenant le match entier est le cas le plus fréquent ·
 * il est sélectionné par défaut et présenté en premier. Les deux autres sont
 * des ALTERNATIVES, pas des égaux · elles n'ajoutent pas une étape au parcours
 * normal, elles ouvrent une porte à côté.
 *
 * DÉCLARÉ AVANT L'UPLOAD, jamais après · c'est ce qui décide du nombre de
 * fichiers à choisir et du nombre de repères à demander. Le poser après
 * obligerait à redemander un fichier déjà envoyé.
 *
 * MODIFIABLE À TOUT MOMENT, et c'est la raison d'être de `video_scope` en base
 * plutôt qu'un dérivé de l'état de stockage · le coach déclare « une mi-temps »,
 * réalise qu'il a le match entier, change son choix — et son fichier reste.
 */

export const SCOPE_FULL_MATCH = 'full_match'
export const SCOPE_SINGLE_HALF = 'single_half'
export const SCOPE_TWO_FILES = 'two_files'

/**
 * Des repères sont demandés DANS LES TROIS CAS · le coach coupe rarement au bon
 * endroit — échauffement, sortie des vestiaires, attente après le coup de
 * sifflet. Ce qui change, c'est leur nombre et leur sens.
 */
export const OPTIONS = [
  {
    valeur: SCOPE_FULL_MATCH,
    titre: 'Un fichier, le match entier',
    detail: 'Tu as filmé sans arrêter la caméra. On découpe les deux périodes pour toi.',
    fichiers: 1,
  },
  {
    valeur: SCOPE_SINGLE_HALF,
    titre: 'Un fichier, une seule période',
    detail: 'Tu n\'as qu\'une mi-temps. Elle est analysée telle quelle, sans découpe.',
    fichiers: 1,
  },
  {
    valeur: SCOPE_TWO_FILES,
    titre: 'Deux fichiers, un par période',
    detail: 'Tu as arrêté la caméra à la pause. Envoie les deux, ils partent l\'un après l\'autre.',
    fichiers: 2,
  },
]

export function fichiersAttendus(scope) {
  return OPTIONS.find(o => o.valeur === scope)?.fichiers ?? 1
}

/**
 * COMBIEN DE REPERES DEMANDER · l'autorite unique, pour les deux ecrans.
 *
 * 4 · match entier · ils pilotent la decoupe (`half_split.py` les lit).
 * 2 · periode seule · debut et fin, les deux autres n'existent pas.
 * 0 · DEUX FICHIERS · **personne ne les lit**.
 *
 * Ce zero est une mesure, pas un avis. En `two_files` · `split-halves` n'est
 * pas appele (garde posee en PARCOURS-VIDEO-DECLARE-001), `recovery.py:143`
 * exige `video_url NOT NULL` qui est vide ici, `matches.py:395` et `:747`
 * aussi, et `create_analysis` n'envoie que l'URL, la qualite, le mode et les
 * deux couleurs — aucun repere. L'agregat MT1+MT2 n'applique aucun decalage.
 * Seul `video_moments.py:496` lit `timeline_full_time`, et uniquement quand
 * `half is None`, ce qui n'est jamais le cas d'un match a deux fichiers.
 *
 * Deux fichiers deja separes sont dans leur forme FINALE · chaque fichier EST
 * une periode. Demander de pointer son debut et sa fin, c'est demander un
 * geste que rien ne consomme · l'analyse sort identique. Cette valeur a
 * longtemps ete `4`, et l'ecran demandait donc quatre reperes d'un match
 * entier sur le PREMIER fichier seul · les trois matchs `two_files` de
 * production n'ont jamais depasse cet ecran (mesure du 11/08 · aucun repere,
 * aucune analyse).
 *
 * ⚠️ Ce zero tombe le jour ou le backend saura ROGNER les fichiers avant
 * analyse · voir `P2-TRIM-DEUX-FICHIERS-001`. Un coach filme souvent quelques
 * minutes d'echauffement avant le coup d'envoi, et elles partent aujourd'hui
 * a l'analyse. Le gain est reel, mais il n'existe pas sans le rognage.
 */
export function reperesAttendus(scope) {
  if (scope === SCOPE_TWO_FILES) return 0
  return scope === SCOPE_SINGLE_HALF ? 2 : 4
}

/**
 * `valeur` a TROIS etats, pas deux — PARCOURS-VIDEO-DECLARE-004.
 *
 * · l'une des trois valeurs · elle est cochee ;
 * · `null` / absente · AUCUNE n'est cochee · le coach n'a pas encore repondu.
 *
 * Le composant forcait `full_match` quand `valeur` etait vide. Sur le wizard
 * c'est le bon defaut — le chemin majoritaire doit rester le plus court, et il
 * passe une valeur explicite depuis son state. Sur la page d'un match qui n'a
 * jamais rien declare, c'etait faux · l'ecran affirmait un choix que personne
 * n'avait fait, et proposait d'envoyer un fichier sur cette base.
 *
 * Le defaut du wizard vit donc dans le WIZARD (`useState(SCOPE_FULL_MATCH)`),
 * pas ici. Un composant d'affichage ne decide pas d'une reponse metier.
 */
/**
 * `sombre` · la modale timeline de MatchDetail est sur fond noir. Sans cette
 * variante, le bloc y rendait de l'encre foncee sur du noir · illisible.
 * Meme structure, seuls les tokens changent — comme `KitColorConfirm`.
 */
const PALETTES = {
  clair:  { encre: T.ink,     attenue: T.muted,                 filet: T.rule,                  fond: T.goldBg },
  sombre: { encre: '#f5f2eb', attenue: 'rgba(245,242,235,0.45)', filet: 'rgba(255,255,255,0.10)', fond: 'rgba(255,255,255,0.05)' },
}

export default function VideoScopeChoice({ valeur, onChange, compact = false, sombre = false }) {
  const p = sombre ? PALETTES.sombre : PALETTES.clair
  const courant = valeur
  return (
    <div data-testid="video-scope" style={{ marginBottom: compact ? 16 : 22 }}>
      <div style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em',
        textTransform: 'uppercase', color: T.gold, marginBottom: 8,
      }}>
        Qu'est-ce que tu as filmé ?
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {OPTIONS.map(o => {
          const actif = courant === o.valeur
          return (
            <label
              key={o.valeur}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 11,
                padding: '12px 14px', cursor: 'pointer',
                background: actif ? p.fond : 'transparent',
                border: `1px solid ${actif ? T.goldBdr : p.filet}`,
              }}
            >
              <input
                type="radio"
                name="video-scope"
                value={o.valeur}
                checked={actif}
                onChange={() => onChange(o.valeur)}
                style={{ marginTop: 2, accentColor: T.gold, flexShrink: 0 }}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{
                  display: 'block', fontFamily: T.mono, fontSize: 11,
                  color: p.encre, fontWeight: actif ? 700 : 400, marginBottom: 3,
                }}>
                  {o.titre}
                </span>
                <span style={{
                  display: 'block', fontFamily: T.mono, fontSize: 10,
                  color: p.attenue, lineHeight: 1.55,
                }}>
                  {o.detail}
                </span>
              </span>
            </label>
          )
        })}
      </div>

      {courant === SCOPE_TWO_FILES && (
        <p style={{
          fontFamily: T.mono, fontSize: 10, color: p.attenue,
          lineHeight: 1.6, margin: '10px 0 0',
        }}>
          Si le second envoi échoue, le premier reste enregistré : tu n'auras
          jamais à le renvoyer.
        </p>
      )}
    </div>
  )
}
